import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ScannerInput from "@/components/ScannerInput";
import { Plus, ArrowDown, ArrowUp, ArrowUUpLeft, ArrowsLeftRight, MagnifyingGlass, Barcode, X as XIcon, UploadSimple, Download } from "@phosphor-icons/react";
import ImportModal from "@/components/ImportModal";
import SearchableSelect from "@/components/SearchableSelect";
import { applyColumnFilters, uniqueValues, sortRows } from "@/components/ColumnFilter";
import ScanButton from "@/components/ScanButton";
import SignatureModal from "@/components/SignatureModal";
import MovementDetailModal from "@/components/MovementDetailModal";
import MovementListTable from "@/components/MovementListTable";
import ODTWizardStep from "@/components/ODTWizardStep";
import SerialPickerModal from "@/components/SerialPickerModal";
import useMovementSubmit from "@/hooks/useMovementSubmit";
import useIsNarrowScreen from "@/hooks/useIsNarrowScreen";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = { entry: "Entrata", exit: "Uscita", return: "Reso", transfer: "Trasferimento", scarico_commessa: "Scarico Commessa" };
const TYPE_ICON = { entry: ArrowDown, exit: ArrowUp, return: ArrowUUpLeft, transfer: ArrowsLeftRight, scarico_commessa: ArrowUp };

export default function Movements() {
  const { user } = useAuth();
  const isNarrowScreen = useIsNarrowScreen(1024);
  const location = useLocation();
  const navigate = useNavigate();
  const isOperator = user?.role === "operator";
  const canWrite = user && user.role !== "viewer";
  const operatorWarehouseId = user?.warehouse_id;
  const [rows, setRows] = useState([]);
  const [signModal, setSignModal] = useState(null); // { movementId, role }
  const [detailId, setDetailId] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [q, setQ] = useState("");

  const [type, setType] = useState(isOperator ? "scarico_commessa" : "entry");
  const [productId, setProductId] = useState("");
  const [fromWh, setFromWh] = useState("");
  const [toWh, setToWh] = useState("");
  const [serialsText, setSerialsText] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [scannerMode, setScannerMode] = useState(false);
  const [scannedList, setScannedList] = useState([]);
  // Imperative ref to the ScannerInput used by the wizard, so we can re-open
  // the camera automatically when advancing to the next product.
  const scannerRef = useRef(null);
  // Product IDs the operator currently has stock of (used to filter product dropdown in scarico_commessa)
  const [operatorStockIds, setOperatorStockIds] = useState(null);
  // Accumulated items when adding multiple products to a single movement
  const [bulkItems, setBulkItems] = useState([]);
  // Pending ODT items (wizard flow). When set, the operator walks through
  // each item one by one — filling in serials/qty — and the main form
  // behaves as a step inside the ODT.
  const [odtQueue, setOdtQueue] = useState([]); // items left to process
  const [odtTotal, setOdtTotal] = useState(0);  // total items in current ODT
  const [lastScan, setLastScan] = useState("");
  const [availableSerials, setAvailableSerials] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFilter, setPickerFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportType, setExportType] = useState("");
  const [exporting, setExporting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [commessaId, setCommessaId] = useState("");
  const [commessaNewMode, setCommessaNewMode] = useState(false);
  const [commessaNameNew, setCommessaNameNew] = useState("");
  // Bulk-entry supplier + unit price (empty for other movement types).
  const [suppliers, setSuppliers] = useState([]);
  const [entrySupplierId, setEntrySupplierId] = useState("");
  const [entryUnitPrice, setEntryUnitPrice] = useState("");
  // Cache of /purchase-prices rows for the currently selected product so we
  // can auto-fill the unit price when the operator picks a supplier.
  const [purchasePrices, setPurchasePrices] = useState([]);

  // When the "Nuovo Movimento" modal is opened as a result of "Evadi ODT",
  // this holds the ODT id so the bulk submit closes the ODT server-side.
  const [transferOrderId, setTransferOrderId] = useState(null);
  // Truncation state: when server-side total > returned, warn the user that the
  // list is capped (so filtering won't silently hide older records).
  const [truncation, setTruncation] = useState(null); // { total, returned }

  const load = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      // Send the search text server-side so we don't miss matches beyond the
      // default fetch limit. Server does an escaped regex on SKU / name /
      // manufacturer_code / serials / DDT / reference.
      if (q && q.trim()) params.q = q.trim();
      const res = await api.get("/movements", { params });
      setRows(res.data);
      const total = parseInt(res.headers?.["x-total-count"] || "0", 10);
      const returned = parseInt(res.headers?.["x-returned-count"] || `${res.data.length}`, 10);
      setTruncation(total > returned ? { total, returned } : null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, w, cust, com, sup] = await Promise.all([
          api.get("/products"), api.get("/warehouses"),
          api.get("/customers"), api.get("/commesse"),
          api.get("/suppliers"),
        ]);
        setProducts(p.data); setWarehouses(w.data);
        setCustomers(cust.data); setCommesse(com.data);
        setSuppliers(sup.data || []);
        // Operators: auto-select their own warehouse as origin (they can't select anything else).
        // If the JWT lacks warehouse_id, use the first (and only) warehouse returned by /warehouses.
        if (isOperator) {
          const opWh = operatorWarehouseId || w.data?.[0]?.id;
          if (opWh) {
            setFromWh(opWh);
            // Load only the products the operator has in stock, so the dropdown for
            // "Scarico Commessa" is filtered to their real availability.
            try {
              const inv = await api.get("/inventory/unified", { params: { warehouse_id: opWh } });
              const ids = new Set((inv.data || []).filter((r) => Number(r.stock || 0) > 0).map((r) => r.product_id));
              setOperatorStockIds(ids);
            } catch { /* silent */ }
          }
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filterType, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // When arriving from "Evadi ODT" (state.odt) prefill the create-movement
  // form with type=transfer, from/to warehouses and the ODT items[]. Waits
  // for products+warehouses to be loaded so the SearchableSelects have their
  // options. Clears location.state on first successful prefill so a refresh
  // does not re-open the modal.
  useEffect(() => {
    const odt = location.state?.odt;
    if (!odt || !products.length || !warehouses.length) return;
    if (transferOrderId === odt.id) return; // already handled
    // Wire up the form
    setType("transfer");
    setFromWh(odt.from_warehouse_id || "");
    setToWh(odt.to_warehouse_id || "");
    setReference(odt.flux_odt_id ? `ODT/${odt.flux_odt_id}` : `ODT/${odt.id.slice(0, 8)}`);
    setNotes(`Evasione ODT · Tecnico ${odt.technician_name}${odt.note ? `\n${odt.note}` : ""}`);
    // Wizard flow: ONE item at a time. The first item fills the main form;
    // the operator confirms it via "Prossimo prodotto" (or "Salva movimento"
    // on the last) and we pull the next item off the queue. bulkItems holds
    // the already-confirmed items that will go into the final grouped POST.
    const items = odt.items || [];
    const [first, ...rest] = items;
    if (first) {
      setProductId(first.product_id);
      if (first.is_serialized === false) {
        setQuantity(first.quantity);
      } else {
        setQuantity(1);
      }
    }
    setSerialsText("");
    setScannedList([]);
    setBulkItems([]);
    setOdtQueue(rest);
    setOdtTotal(items.length);
    setTransferOrderId(odt.id);
    setShowForm(true);
    // Clear the location.state so a refresh won't retrigger this effect.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, products, warehouses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Column filters for the movements table
  const [colFilters, setColFilters] = useState({
    type: new Set(), product_name: new Set(), product_supplier_name: new Set(),
    from_warehouse_name: new Set(), to_warehouse_name: new Set(), destination: new Set(),
    created_by: new Set(),
    created_at_day: new Set(),
    serial_flat: new Set(),
  });
  const [sortCfg, setSortCfg] = useState({ key: "created_at", dir: "desc" });
  const setColFilter = (k) => (v) => setColFilters((f) => ({ ...f, [k]: v }));

  const destinationOf = (r) => r.customer_name || r.commessa_name || (r.reference ? `Rif: ${r.reference}` : "");
  const dayOf = (r) => (r.created_at || "").slice(0, 10); // YYYY-MM-DD
  const serialsJoined = (r) => (r.serial_numbers || []).join(", ");

  const accessors = useMemo(() => ({
    type: (r) => TYPE_LABEL[r.type] || r.type,
    product_name: (r) => r.product_name || "",
    product_supplier_name: (r) => {
      if (r.type === "entry") return r.entry_supplier_name || r.product_supplier_name || "";
      if (Array.isArray(r.bulk_allocations) && r.bulk_allocations.length) {
        const names = [...new Set(r.bulk_allocations.map((a) => a.supplier_name).filter(Boolean))];
        if (names.length) return names.join(" · ");
      }
      return r.product_supplier_name || "";
    },
    from_warehouse_name: (r) => r.from_warehouse_name || "",
    to_warehouse_name: (r) => r.to_warehouse_name || "",
    destination: (r) => destinationOf(r),
    created_at: (r) => r.created_at || "",
    created_at_day: (r) => dayOf(r),
    created_by: (r) => r.created_by_name || r.created_by || "",
    quantity: (r) => (r.group_total_qty ?? r.quantity ?? 0),
    serial_flat: (r) => serialsJoined(r),
  }), []);

  // Custom filter for serials column: match if ANY serial in the movement is selected
  const matchSerialFilter = (r, selected) => {
    if (!selected || selected.size === 0) return true;
    const arr = r.serial_numbers || [];
    return arr.some((s) => selected.has(s));
  };

  const filteredRows = useMemo(() => {
    // 1) Text search across SKU, product_name, manufacturer_code, serials,
    //    ddt_number and reference (mirrors the backend `q` filter so the
    //    client-side dedupe doesn't hide server-side matches).
    const needle = q.trim().toLowerCase();
    let base = !needle ? rows : rows.filter((r) => {
      const s = [
        r.product_sku,
        r.product_name,
        r.product_manufacturer_code,
        r.ddt_number,
        r.reference,
        ...(r.serial_numbers || []),
      ].join(" ").toLowerCase();
      return s.includes(needle);
    });
    // 2) Column filters (excluding serial_flat which needs custom logic)
    const { serial_flat, ...rest } = colFilters; // eslint-disable-line no-unused-vars
    base = applyColumnFilters(base, rest, accessors);
    // 3) Serials filter (any-match)
    base = base.filter((r) => matchSerialFilter(r, colFilters.serial_flat));
    // 4) Group by movement_group_id — keep only the first row of each group,
    //    marking it with `group_size` and `group_children_ids` for display.
    const seenGroups = new Map();
    const grouped = [];
    for (const r of base) {
      const gid = r.movement_group_id;
      if (!gid) { grouped.push(r); continue; }
      if (!seenGroups.has(gid)) {
        seenGroups.set(gid, { ...r, group_size: 1, group_children_ids: [r.id], group_children_products: [{ sku: r.product_sku, name: r.product_name, qty: r.quantity, uom: r.unit_of_measure }], group_all_serials: [...(r.serial_numbers || [])], group_total_qty: r.quantity || 0 });
        grouped.push(seenGroups.get(gid));
      } else {
        const g = seenGroups.get(gid);
        g.group_size += 1;
        g.group_children_ids.push(r.id);
        g.group_children_products.push({ sku: r.product_sku, name: r.product_name, qty: r.quantity, uom: r.unit_of_measure });
        g.group_all_serials = g.group_all_serials.concat(r.serial_numbers || []);
        g.group_total_qty = (g.group_total_qty || 0) + (r.quantity || 0);
      }
    }
    // 5) Sort AFTER grouping so quantity sort uses the group total, not the leader's qty.
    return sortRows(grouped, sortCfg, accessors);
  }, [rows, q, colFilters, accessors, sortCfg]);

  // Warehouses filtered by movement type.
  // Scarico Commessa:
  //  - Operatore: solo il proprio furgone (già disabilitato dal form)
  //  - Schermo piccolo (<1024px, es. smartphone/tablet): solo furgoni
  //  - Admin/Gestore su desktop (anche touchscreen): furgoni + magazzini fisici (raggruppati)
  const fromWarehouseOptions = useMemo(() => {
    if (type !== "scarico_commessa") return warehouses;
    if (isOperator || isNarrowScreen) return warehouses.filter((w) => w.type === "mobile");
    return warehouses;
  }, [warehouses, type, isOperator, isNarrowScreen]);

  const fromWarehouseGroups = useMemo(() => {
    if (type !== "scarico_commessa" || isOperator || isNarrowScreen) return null;
    const mobile = fromWarehouseOptions.filter((w) => w.type === "mobile");
    const physical = fromWarehouseOptions.filter((w) => w.type !== "mobile");
    return { mobile, physical };
  }, [fromWarehouseOptions, type, isOperator, isNarrowScreen]);

  let referencePlaceholder = "Ordine, tecnico...";
  if (type === "entry") referencePlaceholder = "Es. DDT-2026-001";
  else if (type === "exit") referencePlaceholder = "Note aggiuntive sull'uscita...";

  const uniques = useMemo(() => {
    // Dynamic uniques for standard columns: for each column, use rows filtered
    // by every OTHER active filter (plus the custom serial matcher).
    const applyMovFilters = (baseRows, filt) => {
      const { serial_flat, ...rest } = filt;
      const step1 = applyColumnFilters(baseRows, rest, accessors);
      return step1.filter((r) => matchSerialFilter(r, serial_flat));
    };
    const out = {};
    for (const key of Object.keys(colFilters)) {
      if (key === "serial_flat") continue;
      const others = { ...colFilters, [key]: new Set() };
      out[key] = uniqueValues(applyMovFilters(rows, others), accessors[key]);
    }
    // Custom serial_flat: unique serials appearing in rows that pass every
    // OTHER filter (so filtering by warehouse/type shrinks the serial list too).
    const otherSerial = { ...colFilters, serial_flat: new Set() };
    const baseSerial = applyMovFilters(rows, otherSerial);
    const serialSet = new Set();
    for (const r of baseSerial) for (const s of (r.serial_numbers || [])) if (s) serialSet.add(s);
    out.serial_flat = Array.from(serialSet).sort();
    return out;
  }, [rows, colFilters, accessors]);

  const needsFrom = type === "exit" || type === "transfer" || type === "scarico_commessa";
  const needsTo = type === "entry" || type === "return" || type === "transfer";
  const isScaricoCommessa = type === "scarico_commessa";

  const selectedProduct = products.find((p) => p.id === productId);
  const isBulk = selectedProduct && selectedProduct.is_serialized === false;
  const selectedFromWh = warehouses.find((w) => w.id === fromWh);

  // Load available serials for the picker.
  // - exit / transfer: in_stock serials for the product (filtered by from_warehouse if set)
  // - return: serials currently "out" (they are the ones that CAN be returned)
  // - entry: none (new serials, don't exist yet)
  useEffect(() => {
    if (!productId || isBulk || type === "entry") { setAvailableSerials([]); return; }
    const wantedStatus = type === "return" ? "out" : "in_stock";
    (async () => {
      try {
        const params = { product_id: productId, status: wantedStatus };
        if (wantedStatus === "in_stock" && fromWh) params.warehouse_id = fromWh;
        const { data } = await api.get("/serials/available", { params });
        setAvailableSerials(data);
      } catch (e) {
        console.warn("Errore caricamento seriali disponibili", e);
        setAvailableSerials([]);
      }
    })();
  }, [productId, fromWh, isBulk, type]);

  // Load purchase-price rows for the current product (bulk entry only).
  // Used to auto-fill the unit_price when the operator picks a supplier.
  useEffect(() => {
    if (!productId || !isBulk || type !== "entry") {
      setPurchasePrices([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/purchase-prices", { params: { product_id: productId } });
        setPurchasePrices(data || []);
      } catch (e) {
        console.warn("Errore caricamento listino acquisto", e);
        setPurchasePrices([]);
      }
    })();
  }, [productId, isBulk, type]);

  // Auto-fill unit_price whenever the chosen supplier matches a listino row.
  useEffect(() => {
    const hit = entrySupplierId ? purchasePrices.find((pp) => pp.supplier_id === entrySupplierId) : null;
    setEntryUnitPrice(hit ? String(hit.price ?? "") : "");
  }, [entrySupplierId, purchasePrices]);

  // Resets the form fields after a successful submit. `fullReset` is used
  // after a bulk/ODT submit (clears bulkItems + ODT queue + closes modal);
  // the single-movement path keeps the modal lifecycle simpler.
  const resetForm = ({ fullReset }) => {
    setShowForm(false);
    setBulkItems([]);
    setTransferOrderId(null);
    setOdtQueue([]);
    setOdtTotal(0);
    if (fullReset) return;
    if (isOperator) {
      setType("scarico_commessa");
      const opWh = operatorWarehouseId || warehouses[0]?.id || "";
      setFromWh(opWh);
    } else {
      setType("entry"); setFromWh("");
    }
    setProductId(""); setToWh("");
    setSerialsText(""); setReference(""); setNotes("");
    setScannedList([]); setLastScan(""); setQuantity(1);
    setCustomerId(""); setCommessaId("");
    setCommessaNewMode(false); setCommessaNameNew("");
    setEntrySupplierId(""); setEntryUnitPrice("");
    setPurchasePrices([]);
    // Reload commesse in case a new one was created on-the-fly
    api.get("/commesse").then(({ data }) => setCommesse(data)).catch((e) => console.warn("Reload commesse after movement failed", e));
  };

  const { submit, submitting } = useMovementSubmit({
    getState: () => ({
      type, productId, fromWh, toWh, serialsText, scannedList, scannerMode,
      quantity, reference, notes, customerId, commessaId, commessaNewMode,
      commessaNameNew, bulkItems, transferOrderId, selectedFromWh, selectedProduct,
      entrySupplierId, entryUnitPrice,
    }),
    resetForm,
    reloadList: load,
    onSuccess: ({ bulk, lead, type: movType }) => {
      if (bulk) {
        if (movType === "exit" || movType === "transfer" || movType === "scarico_commessa") {
          setSignModal({ movementId: lead.id, role: "destinatario", movementType: lead.type, ddtNumber: lead.ddt_number });
        }
      } else {
        setSignModal({ movementId: lead.id, role: "destinatario", movementType: lead.type, ddtNumber: lead.ddt_number });
      }
    },
  });

  const addSerialsFromPicker = (picked) => {
    // Merge picked serials into the appropriate list (scanner mode or textarea)
    if (scannerMode) {
      const merged = Array.from(new Set([...scannedList, ...picked]));
      setScannedList(merged);
    } else {
      const existing = serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      const merged = Array.from(new Set([...existing, ...picked]));
      setSerialsText(merged.join("\n"));
    }
    setPickerOpen(false);
    setPickerFilter("");
    toast.success(`${picked.length} seriali aggiunti`);
  };

  const handleScan = (code) => {
    const clean = code.trim();
    if (!clean) return;
    if (scannedList.includes(clean)) {
      toast.error(`Seriale ${clean} già scansionato`);
      return;
    }
    setScannedList((prev) => [...prev, clean]);
    setLastScan(clean);
    // Beep sound (short audio feedback)
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880; gain.gain.value = 0.05;
        osc.start(); osc.stop(ctx.currentTime + 0.08);
      }
    } catch (e) { console.warn("Beep audio unavailable", e); }
  };

  const removeScanned = (code) => {
    setScannedList((prev) => prev.filter((s) => s !== code));
  };

  const downloadPdf = async (m) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/movements/${m.id}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("errore");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `movimento_${m.type}_${m.id.slice(0, 8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("PDF download failed", e); toast.error("Errore generazione PDF"); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (exportFrom) params.append("date_from", exportFrom);
      if (exportTo) params.append("date_to", exportTo);
      if (exportType) params.append("type", exportType);
      const qs = params.toString();
      const res = await fetch(`${API_BASE}/export/movements${qs ? `?${qs}` : ""}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore export");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = (exportFrom || exportTo) ? `_${exportFrom || "inizio"}_${exportTo || "oggi"}` : "";
      a.href = url; a.download = `movimenti${suffix}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success("Export scaricato");
    } catch (e) {
      console.error("Export failed", e);
      toast.error(e.message || "Errore export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Movimenti"
        subtitle="Entrate, uscite, resi e trasferimenti tra magazzini"
        action={
          <div className="flex gap-2">
            <button data-testid="export-movements-btn" onClick={() => setExportOpen(true)} className="text-sm px-3 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors flex items-center gap-2">
              <Download size={16} /> <span className="hidden sm:inline">Export</span>
            </button>
            {canWrite && (
              <button data-testid="import-movements-btn" onClick={() => setImportOpen(true)} className="text-sm px-3 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors flex items-center gap-2">
                <UploadSimple size={16} /> <span className="hidden sm:inline">Import</span>
              </button>
            )}
            {canWrite && (
              <button data-testid="new-movement-btn" onClick={() => setShowForm(true)} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2">
                <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Nuovo Movimento</span><span className="sm:hidden">Nuovo</span>
              </button>
            )}
          </div>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="text-neutral-400" />
            <input data-testid="movements-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca SKU, prodotto, cod. produttore o seriale..." className="flex-1 outline-none text-sm bg-transparent" />
          </div>
          <div className="flex border border-neutral-200 bg-white flex-wrap">
            {[["", "Tutti"], ["entry", "Entrate"], ["exit", "Uscite"], ["return", "Resi"], ["transfer", "Trasf."], ["scarico_commessa", "Scar. Commessa"]].map(([v, l]) => (
              <button
                key={v}
                data-testid={`filter-${v || "all"}`}
                onClick={() => setFilterType(v)}
                className={`px-3 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${filterType === v ? "bg-[#002FA7] text-white" : "text-neutral-600 hover:bg-neutral-50"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {truncation && (
          <div
            data-testid="movements-truncation-banner"
            className="mb-3 flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm"
          >
            <div className="font-bold text-amber-800 uppercase tracking-wider text-[11px]">Risultati parziali</div>
            <div className="text-amber-900">
              Visualizzati i {truncation.returned.toLocaleString("it-IT")} movimenti più recenti su {truncation.total.toLocaleString("it-IT")} totali.
              Restringi con i filtri (tipo, data, ricerca) per vedere anche quelli meno recenti.
            </div>
          </div>
        )}

        <MovementListTable
          rows={rows}
          filteredRows={filteredRows}
          uniques={uniques}
          colFilters={colFilters}
          setColFilter={setColFilter}
          sortCfg={sortCfg}
          setSortCfg={setSortCfg}
          onRowClick={(id) => setDetailId(id)}
          onSign={(r) => setSignModal({ movementId: r.id, role: "destinatario" })}
          onDownloadPdf={downloadPdf}
        />
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="movement-form">
            <div className="p-4 md:p-6 border-b border-neutral-200 sticky top-0 bg-white">
              <div className="label-eyebrow mb-1">Registra</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Nuovo Movimento</h2>
              <ODTWizardStep
                transferOrderId={transferOrderId}
                confirmedCount={bulkItems.length}
                totalCount={odtTotal}
              />
            </div>
            <div className="p-4 md:p-6 space-y-4">
              <div>
                <span className="label-eyebrow block mb-2">Tipo movimento *</span>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {(isOperator ? ["scarico_commessa"] : ["entry", "exit", "return", "transfer", "scarico_commessa"]).map((t) => {
                    const Icon = TYPE_ICON[t];
                    return (
                      <button
                        key={t}
                        type="button"
                        data-testid={`type-${t}`}
                        onClick={() => setType(t)}
                        className={`flex items-center justify-center gap-1.5 py-2.5 border transition-colors text-xs font-medium ${type === t ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}
                      >
                        <Icon size={14} weight="bold" /> {TYPE_LABEL[t]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="label-eyebrow">Prodotto *</span>
                  <ScanButton
                    testid="product-scan-btn"
                    title="Scansiona codice prodotto"
                    scannerTitle="Scanner codice prodotto"
                    label="Codice interno (SKU) o codice produttore"
                    placeholder="Scansiona o digita codice e premi Invio..."
                    onScan={async (code) => {
                      try {
                        const { data } = await api.get(`/products/lookup`, { params: { code } });
                        setProductId(data.id);
                        toast.success(`Prodotto selezionato: ${data.name}`);
                      } catch (err) {
                        toast.error(formatApiError(err));
                      }
                    }}
                  />
                </div>
                <SearchableSelect
                  testid="movement-product"
                  required
                  value={productId}
                  onChange={setProductId}
                  placeholder="— Seleziona prodotto —"
                  emptyText={isOperator && type === "scarico_commessa" ? "Nessun prodotto disponibile nel tuo magazzino" : "Nessun prodotto corrisponde alla ricerca"}
                  options={products
                    .filter((p) => !(isOperator && type === "scarico_commessa" && operatorStockIds) || operatorStockIds.has(p.id))
                    .map((p) => ({
                    value: p.id,
                    label: `${p.sku} — ${p.name}`,
                    sublabel: [
                      p.manufacturer_code && `cod. ${p.manufacturer_code}`,
                      p.category_name && `cat. ${p.category_name}`,
                      p.supplier_name && `forn. ${p.supplier_name}`,
                      p.is_serialized === false ? "a quantità" : "a seriale",
                      typeof p.stock === "number" && `giacenza: ${p.stock}`,
                    ].filter(Boolean).join(" · "),
                    keywords: [p.manufacturer_code, p.category_name, p.supplier_name].filter(Boolean),
                  }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {needsFrom && (
                  <label className="block">
                    <span className="label-eyebrow block mb-2">Da magazzino *</span>
                    <select required data-testid="movement-from-wh" value={fromWh} onChange={(e) => setFromWh(e.target.value)} disabled={isOperator} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-neutral-100 disabled:cursor-not-allowed">
                      <option value="">— Seleziona —</option>
                      {fromWarehouseGroups ? (
                        <>
                          {fromWarehouseGroups.mobile.length > 0 && (
                            <optgroup label="Furgoni">
                              {fromWarehouseGroups.mobile.map((w) => (<option key={w.id} value={w.id}>🚐 {w.name}</option>))}
                            </optgroup>
                          )}
                          {fromWarehouseGroups.physical.length > 0 && (
                            <optgroup label="Magazzini">
                              {fromWarehouseGroups.physical.map((w) => (<option key={w.id} value={w.id}>🏢 {w.name}</option>))}
                            </optgroup>
                          )}
                        </>
                      ) : (
                        fromWarehouseOptions.map((w) => (<option key={w.id} value={w.id}>{w.type === "mobile" ? "🚐" : "🏢"} {w.name}</option>))
                      )}
                    </select>
                  </label>
                )}
                {needsTo && (
                  <label className="block">
                    <span className="label-eyebrow block mb-2">A magazzino *</span>
                    <select required data-testid="movement-to-wh" value={toWh} onChange={(e) => setToWh(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                      <option value="">— Seleziona —</option>
                      {warehouses.map((w) => (<option key={w.id} value={w.id}>{w.type === "mobile" ? "🚐" : "🏢"} {w.name}</option>))}
                    </select>
                  </label>
                )}
                {/* Destinatari accanto al magazzino */}
                {type === "exit" && (
                  <div className="block">
                    <span className="label-eyebrow block mb-2">Cliente destinatario *</span>
                    <SearchableSelect
                      testid="movement-customer"
                      required
                      value={customerId}
                      onChange={setCustomerId}
                      placeholder="— Seleziona cliente —"
                      emptyText="Nessun cliente. Crea prima l'anagrafica in Clienti."
                      options={customers.map((c) => ({
                        value: c.id,
                        label: c.name,
                        sublabel: [c.address, c.phone].filter(Boolean).join(" · "),
                        keywords: [c.contact, c.email, c.phone, c.address].filter(Boolean),
                      }))}
                    />
                  </div>
                )}
                {type === "return" && (
                  <div className="block">
                    <span className="label-eyebrow block mb-2">Cliente che restituisce *</span>
                    <SearchableSelect
                      testid="movement-return-customer"
                      required
                      value={customerId}
                      onChange={setCustomerId}
                      placeholder="— Seleziona cliente —"
                      emptyText="Nessun cliente. Crea prima l'anagrafica in Clienti."
                      options={customers.map((c) => ({
                        value: c.id,
                        label: c.name,
                        sublabel: [c.address, c.phone].filter(Boolean).join(" · "),
                        keywords: [c.contact, c.email, c.phone, c.address].filter(Boolean),
                      }))}
                    />
                  </div>
                )}
                {type === "scarico_commessa" && (
                  <div className="block">
                    <div className="label-eyebrow mb-2 flex items-center justify-between">
                      <span>A Commessa *</span>
                      {commessaNewMode && (
                        <button type="button" onClick={() => { setCommessaNewMode(false); setCommessaNameNew(""); }} className="text-[10px] text-[#002FA7] hover:underline normal-case font-normal">← seleziona da elenco</button>
                      )}
                    </div>
                    {commessaNewMode ? (
                      <input
                        required
                        autoFocus
                        data-testid="movement-commessa-new"
                        value={commessaNameNew}
                        onChange={(e) => setCommessaNameNew(e.target.value)}
                        placeholder="Nome della nuova commessa..."
                        className="w-full px-3 py-2 border-2 border-emerald-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    ) : (
                      <SearchableSelect
                        testid="movement-commessa"
                        required
                        value={commessaId}
                        onChange={setCommessaId}
                        placeholder="— Seleziona commessa —"
                        emptyText="Nessuna commessa. Usa il tasto qui sotto per crearne una."
                        options={commesse.map((c) => ({
                          value: c.id,
                          label: c.name,
                          sublabel: [c.reference && `rif. ${c.reference}`, c.address].filter(Boolean).join(" · "),
                          keywords: [c.reference, c.address].filter(Boolean),
                        }))}
                      />
                    )}
                    {!commessaNewMode && (
                      <button
                        type="button"
                        data-testid="commessa-new-mode-btn"
                        onClick={() => { setCommessaNewMode(true); setCommessaId(""); }}
                        className="mt-1.5 text-xs text-emerald-700 hover:underline flex items-center gap-1"
                      >
                        + Non è in elenco? Crea una nuova commessa
                      </button>
                    )}
                  </div>
                )}
              </div>

              {isBulk ? (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="label-eyebrow">Quantità *</span>
                    <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 uppercase font-semibold tracking-wider">
                      Prodotto a quantità
                    </span>
                  </div>
                  <div className="flex items-stretch">
                    <input
                      required
                      data-testid="movement-quantity"
                      type="number"
                      min="1"
                      step="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="flex-1 px-3 py-3 border-2 border-[#002FA7] border-r-0 text-lg font-mono font-bold focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                    <div
                      data-testid="movement-quantity-uom"
                      title="Unità di misura del prodotto"
                      className="px-4 py-3 border-2 border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] text-lg font-mono font-bold flex items-center min-w-[70px] justify-center"
                    >
                      {selectedProduct?.unit_of_measure || "pz"}
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {type === "entry" && "Quantità che entra in magazzino"}
                    {type === "exit" && "Quantità che esce verso esterno"}
                    {type === "return" && "Quantità che rientra da esterno"}
                    {type === "transfer" && "Quantità da trasferire tra magazzini"}
                  </div>

                  {type === "entry" && (
                    <div className="mt-4 p-3 border border-[#002FA7]/30 bg-[#002FA7]/5 space-y-3" data-testid="bulk-entry-supplier-block">
                      <div className="text-[11px] uppercase tracking-wider font-bold text-[#002FA7]">
                        Lotto di ingresso
                      </div>
                      <div data-testid="movement-entry-supplier-panel">
                        <span className="label-eyebrow block mb-2">Fornitore del lotto *</span>
                        <SearchableSelect
                          testid="movement-entry-supplier"
                          value={entrySupplierId}
                          onChange={setEntrySupplierId}
                          placeholder="— Seleziona fornitore —"
                          options={suppliers.map((s) => {
                            const hit = purchasePrices.find((pp) => pp.supplier_id === s.id);
                            return {
                              value: s.id,
                              label: s.name,
                              sublabel: hit ? `Listino: € ${Number(hit.price).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "Nessun listino configurato",
                              keywords: [s.name, s.email, s.phone].filter(Boolean),
                            };
                          })}
                        />
                      </div>
                      <label className="block">
                        <span className="label-eyebrow block mb-2">Prezzo unitario (€)</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid="movement-entry-unit-price"
                          value={entryUnitPrice}
                          onChange={(e) => setEntryUnitPrice(e.target.value)}
                          placeholder="0,00"
                          className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                        {(() => {
                          const hit = entrySupplierId ? purchasePrices.find((pp) => pp.supplier_id === entrySupplierId) : null;
                          if (hit) return <p className="text-xs text-neutral-500 mt-1">Auto-compilato dal listino acquisto (modificabile).</p>;
                          if (entrySupplierId) return <p className="text-xs text-amber-700 mt-1">Nessun listino per questa coppia prodotto · fornitore. Inserisci manualmente il prezzo.</p>;
                          return null;
                        })()}
                      </label>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <span className="label-eyebrow">Numeri Seriali *</span>
                    <div className="flex gap-2 flex-wrap">
                      {type !== "entry" && availableSerials.length > 0 && (
                        <button
                          type="button"
                          data-testid="serial-picker-btn"
                          onClick={() => { setPickerOpen(true); setPickerFilter(""); }}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-emerald-600 text-emerald-700 hover:bg-emerald-50 transition-colors"
                        >
                          {type === "return" ? "Scegli tra quelli usciti" : "Scegli dai disponibili"} ({availableSerials.length})
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid="scanner-mode-toggle"
                        onClick={() => setScannerMode((s) => !s)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border transition-colors ${scannerMode ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-neutral-300 hover:bg-neutral-50"}`}
                      >
                        <Barcode size={14} weight="bold" />
                        {scannerMode ? "Scanner ON" : "Scanner"}
                      </button>
                    </div>
                  </div>

                  {type !== "entry" && productId && (() => {
                    // Show the "not available" banner only when the operator
                    // has already typed/scanned serials that are NOT present
                    // in the available list. This avoids a confusing warning
                    // right after a fresh entry (race with /serials/available).
                    const typedSerials = scannerMode
                      ? scannedList
                      : serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
                    if (typedSerials.length === 0) return null;
                    const availableSet = new Set(availableSerials.map((s) => s.serial_number));
                    const missing = typedSerials.filter((s) => !availableSet.has(s));
                    if (missing.length === 0) return null;
                    const preview = missing.slice(0, 3).join(", ") + (missing.length > 3 ? ` (+${missing.length - 3})` : "");
                    return (
                      <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="movement-serials-unavailable">
                        {type === "return"
                          ? `Seriali non in stato "Uscita": ${preview}`
                          : `Seriali non disponibili in giacenza: ${preview}`}
                      </div>
                    );
                  })()}

                  {scannerMode ? (
                    <div className="space-y-3">
                      <ScannerInput
                        ref={scannerRef}
                        onScan={handleScan}
                        autoFocus
                        lastScanned={lastScan}
                        label={`Scansiona seriale · ${scannedList.length} pz raccolti`}
                        placeholder="Scansiona con scanner USB o digita e premi Invio..."
                        testid="movement-scanner"
                      />
                      {scannedList.length > 0 && (
                        <div className="border border-neutral-200 max-h-64 overflow-y-auto">
                          <div className="bg-neutral-50 px-3 py-2 label-eyebrow border-b border-neutral-200 flex items-center justify-between">
                            <span>Seriali scansionati ({scannedList.length})</span>
                            <button type="button" onClick={() => setScannedList([])} className="text-red-600 hover:underline normal-case text-[10px]">Svuota</button>
                          </div>
                          <ul>
                            {scannedList.map((s, i) => (
                              <li key={s} className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                                <span className="font-mono text-sm">
                                  <span className="text-neutral-400 mr-2">{String(i + 1).padStart(3, "0")}.</span>{s}
                                </span>
                                <button type="button" data-testid={`remove-scanned-${s}`} onClick={() => removeScanned(s)} className="p-1 hover:bg-red-100 text-red-600 transition-colors">
                                  <XIcon size={12} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      required={!scannerMode}
                      data-testid="movement-serials"
                      value={serialsText}
                      onChange={(e) => setSerialsText(e.target.value)}
                      rows={5}
                      placeholder={"SN001\nSN002\nSN003"}
                      className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                  )}

                  <div className="text-xs text-neutral-500 mt-1">
                    {type === "entry" && "Nuovi seriali che entrano in magazzino"}
                    {type === "exit" && "Seriali che escono verso esterno (cliente/installazione)"}
                    {type === "return" && "Seriali che rientrano da esterno (devono essere in stato Uscita)"}
                    {type === "transfer" && "Trasferimento tra magazzini/furgoni (es. carico furgone tecnico)"}
                  </div>
                </div>
              )}

              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">{type === "entry" ? "DDT" : "Riferimento"}</span>
                <div className="flex items-stretch gap-0">
                  <input data-testid="movement-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={referencePlaceholder} className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                  <ScanButton testid="movement-reference-scan" title="Scansiona DDT / Riferimento" scannerTitle={type === "entry" ? "Scanner DDT" : "Scanner Riferimento"} onScan={(code) => setReference(code)} className="border-l-0" />
                </div>
              </label>

              {/* Multi-product: accumulator list + "Aggiungi altro prodotto" button */}
              <div className="md:col-span-2 border-t border-dashed border-neutral-300 pt-3">
                {bulkItems.length > 0 && (
                  <div className="mb-3">
                    <div className="label-eyebrow mb-1.5 text-[#002FA7]">Prodotti aggiunti al movimento raggruppato ({bulkItems.length})</div>
                    <div className="space-y-1.5">
                      {bulkItems.map((it, idx) => {
                        const p = products.find((x) => x.id === it.product_id);
                        return (
                          <div key={idx} data-testid={`bulk-item-${idx}`} className="flex items-center justify-between p-2 border border-[#002FA7]/30 bg-[#002FA7]/5 text-sm">
                            <span className="truncate">
                              <b>{p?.sku || it.product_id}</b> — {p?.name || "?"}
                              <span className="ml-2 text-xs text-neutral-600 font-mono">
                                {it.serial_numbers && it.serial_numbers.length > 0 ? `${it.serial_numbers.length} seriali` : `qtà ${it.quantity}`}
                              </span>
                            </span>
                            <button type="button" data-testid={`bulk-item-remove-${idx}`}
                              onClick={() => setBulkItems(bulkItems.filter((_, i) => i !== idx))}
                              className="ml-2 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50">Rimuovi</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="movement-add-another-product"
                  disabled={!productId || (isBulk ? (!quantity || parseInt(quantity) <= 0) : ((scannerMode ? scannedList : serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean)).length === 0))}
                  onClick={() => {
                    const serialsFromText = serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
                    const serials = scannerMode ? scannedList : serialsFromText;
                    if (isBulk && type === "entry" && !entrySupplierId) { toast.error("Seleziona il fornitore del lotto"); return; }
                    const price = parseFloat(entryUnitPrice);
                    const newItem = isBulk
                      ? {
                          product_id: productId, quantity: parseInt(quantity) || 1, serial_numbers: null,
                          ...(type === "entry" ? { supplier_id: entrySupplierId, unit_price: Number.isNaN(price) ? null : price } : {}),
                        }
                      : { product_id: productId, quantity: serials.length, serial_numbers: serials };
                    setBulkItems([...bulkItems, newItem]);
                    setEntrySupplierId(""); setEntryUnitPrice("");
                    // Reset product-specific fields, keep common ones (from/to warehouse, customer, etc.)
                    setProductId(""); setQuantity(1); setSerialsText(""); setScannedList([]);
                    toast.success("Prodotto aggiunto — seleziona il prossimo prodotto");
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={16} weight="bold" />
                  Aggiungi altro prodotto al movimento
                </button>
                <p className="text-[11px] text-neutral-500 mt-1 text-center">
                  Utile per raggruppare più prodotti/seriali sotto lo stesso DDT/documento
                </p>
              </div>

              <label className="block">
                <span className="label-eyebrow block mb-2">Note</span>
                <textarea data-testid="movement-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
            </div>
            <div className="p-4 md:p-6 pt-0 flex justify-end gap-2 border-t border-neutral-200 mt-4 sticky bottom-0 bg-white">
              <button type="button" onClick={() => { setShowForm(false); setBulkItems([]); setTransferOrderId(null); setOdtQueue([]); setOdtTotal(0); }} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              {odtQueue.length > 0 && (
                <button
                  type="button"
                  data-testid="odt-wizard-next"
                  disabled={
                    !productId || (isBulk
                      ? (!quantity || parseInt(quantity) <= 0)
                      : ((scannerMode ? scannedList : serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean)).length === 0)
                    )
                  }
                  onClick={() => {
                    const serialsFromText = serialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
                    const serials = scannerMode ? scannedList : serialsFromText;
                    const newItem = isBulk
                      ? { product_id: productId, quantity: parseInt(quantity) || 1, serial_numbers: null }
                      : { product_id: productId, quantity: serials.length, serial_numbers: serials };
                    const nextBulk = [...bulkItems, newItem];
                    const [nextItem, ...stillPending] = odtQueue;
                    setBulkItems(nextBulk);
                    setOdtQueue(stillPending);
                    // Prefill the main form with the next ODT item
                    setProductId(nextItem.product_id);
                    if (nextItem.is_serialized === false) setQuantity(nextItem.quantity);
                    else setQuantity(1);
                    setSerialsText(""); setScannedList([]);
                    toast.success(`Prodotto confermato · prossimo: ${nextItem.sku || nextItem.name}`);
                    // Auto-reopen the camera on mobile so the operator can keep
                    // scanning serials for the next product without having to
                    // tap the camera icon again. Preserves the user-gesture
                    // chain by calling it synchronously from the click.
                    if (scannerMode && scannerRef.current?.isMobile && nextItem.is_serialized !== false) {
                      try { scannerRef.current.openCamera(); } catch { /* silent */ }
                    }
                  }}
                  className="px-4 py-2 text-sm border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prossimo prodotto →
                </button>
              )}
              <button data-testid="movement-submit" type="submit" disabled={submitting || odtQueue.length > 0} title={odtQueue.length > 0 ? "Compila ancora gli ultimi prodotti dell'ODT prima di salvare" : undefined} className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors disabled:opacity-50">
                {submitting ? "..." : (odtTotal > 0 ? "Salva movimento ODT" : "Registra")}
              </button>
            </div>
          </form>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/movements/import"
        templateEndpoint="/import/template/movements"
        title="Movimenti (Entrate)"
        testidPrefix="movements-import"
        onSuccess={load}
      />

      <SerialPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        serials={availableSerials}
        filter={pickerFilter}
        setFilter={setPickerFilter}
        onConfirm={addSerialsFromPicker}
      />

      {exportOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-md max-h-[95vh] overflow-y-auto" data-testid="export-modal">
            <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1">Export XLSX</div>
                <h2 className="font-display font-bold text-xl tracking-tight">Movimenti</h2>
              </div>
              <button onClick={() => setExportOpen(false)} className="p-1.5 hover:bg-neutral-100 transition-colors"><XIcon size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="label-eyebrow block mb-2">Dal</span>
                  <input data-testid="export-date-from" type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                </label>
                <label className="block">
                  <span className="label-eyebrow block mb-2">Al</span>
                  <input data-testid="export-date-to" type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                </label>
              </div>
              <label className="block">
                <span className="label-eyebrow block mb-2">Tipo (opzionale)</span>
                <select data-testid="export-type" value={exportType} onChange={(e) => setExportType(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  <option value="">— Tutti i tipi —</option>
                  <option value="entry">Entrate</option>
                  <option value="exit">Uscite</option>
                  <option value="return">Resi</option>
                  <option value="transfer">Trasferimenti</option>
                  <option value="scarico_commessa">Scarico Commessa</option>
                </select>
              </label>
              <div className="text-xs text-neutral-500">
                Lascia vuoto per esportare tutti i movimenti. Max 50.000 righe per file.
              </div>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2 border-t border-neutral-200 mt-4">
              <button type="button" onClick={() => setExportOpen(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button
                data-testid="export-submit"
                onClick={doExport}
                disabled={exporting}
                className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Download size={14} /> {exporting ? "Export..." : "Scarica XLSX"}
              </button>
            </div>
          </div>
        </div>
      )}

      <SignatureModal
        open={!!signModal}
        onClose={() => setSignModal(null)}
        movementId={signModal?.movementId}
        role={signModal?.role}
        movementType={signModal?.movementType || rows.find((r) => r.id === signModal?.movementId)?.type}
        ddtNumber={signModal?.ddtNumber || rows.find((r) => r.id === signModal?.movementId)?.ddt_number}
        onSigned={async () => await load()}
      />
      <MovementDetailModal
        open={!!detailId}
        movementId={detailId}
        onClose={() => setDetailId(null)}
        onUpdated={load}
        customers={customers}
        commesse={commesse}
        products={products}
        warehouses={warehouses}
      />
    </div>
  );
}
